import React, {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

interface CategoryMenuBarProps {
  setSectionVisibility: (sectionVisibility: SectionVisibility) => void
  setCurrentSectionIndex: Dispatch<SetStateAction<number>>
  currentSectionIndex: number
  setAutoScrollActive: Dispatch<SetStateAction<boolean>>
  autoScrollActive: boolean
}

interface SectionVisibility {
  sectionIndex: number
  percentageVisible: number
}

const defaultCategoryMenuBarContextValue: CategoryMenuBarProps = {
  setSectionVisibility: () => {},
  setCurrentSectionIndex: () => 0,
  currentSectionIndex: 0,
  setAutoScrollActive: () => false,
  autoScrollActive: false,
}

const CategoryMenuBarContext = createContext<CategoryMenuBarProps>(
  defaultCategoryMenuBarContextValue,
)

export const useCategoryMenuBarContext = () =>
  useContext(CategoryMenuBarContext)

export const CategoryMenuBarProvider = ({ children }: PropsWithChildren) => {
  const [visibleSections, setVisibleSections] = useState<SectionVisibility[]>(
    [],
  )

  const [currentSectionIndex, setCurrentSectionIndex] = useState(0)

  const [autoScrollActive, setAutoScrollActive] = useState(false)

  const setSectionVisibility = useCallback(
    (sectionVisibility: SectionVisibility) => {
      const { sectionIndex, percentageVisible } = sectionVisibility
      setVisibleSections((prevVisibleSections) => {
        const visibleSections = prevVisibleSections.filter(
          (s) => s.sectionIndex !== sectionIndex,
        )
        if (percentageVisible > 0) {
          visibleSections.push(sectionVisibility)
        }
        visibleSections.sort(
          (a, b) =>
            b.percentageVisible - a.percentageVisible ||
            a.sectionIndex - b.sectionIndex,
        )
        return visibleSections
      })
    },
    [],
  )

  useEffect(() => {
    if (autoScrollActive) return
    setCurrentSectionIndex(visibleSections[0]?.sectionIndex ?? 0)
  }, [autoScrollActive, visibleSections])

  return (
    <CategoryMenuBarContext.Provider
      value={{
        setSectionVisibility,
        setCurrentSectionIndex,
        currentSectionIndex,
        setAutoScrollActive,
        autoScrollActive,
      }}
    >
      {children}
    </CategoryMenuBarContext.Provider>
  )
}

const NAVBAR_HEIGHT = 112
const NAVBAR_BOTTOM_PADDING = 20
const NAVBAR_TOTAL_HEIGHT = NAVBAR_HEIGHT + NAVBAR_BOTTOM_PADDING
const FOOTER_HEIGHT = 80

type CategoryMenuBarSyncProps = { sectionIndex: number }

export const CategoryMenuBarSync = ({
  sectionIndex,
  children,
}: PropsWithChildren<CategoryMenuBarSyncProps>) => {
  const containerRef = useRef<HTMLDivElement | null>(null)

  const { setSectionVisibility } = useCategoryMenuBarContext()

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // Just in case we observe multiple changes order by most recent first
        const [{ intersectionRatio: percentageVisible }] = entries.sort(
          (a, b) => b.time - a.time,
        )
        setSectionVisibility({ sectionIndex, percentageVisible })
      },
      {
        rootMargin: `-${NAVBAR_TOTAL_HEIGHT}px 0px -${FOOTER_HEIGHT}px 0px`,
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
      },
    )
    observer.observe(containerRef.current!)
    return () => {
      // Clear the visibility value on sectionIndex change
      setSectionVisibility({ sectionIndex, percentageVisible: 0 })
      observer.disconnect()
    }
  }, [sectionIndex, setSectionVisibility])

  return <div ref={containerRef}>{children}</div>
}
